/**
 * Drawer context — the GLOBAL right drawer (Elevate-sidepane pattern) shows
 * cross-entity lenses for whatever the user is looking at. A page registers
 * its record; the shell's drawer binds its tenants (timeline · insights) to
 * it. `ctx` optionally narrows the timeline to one related record (a deal
 * clicked in a subgrid). Same module-store pattern as paletteScope.
 */
import { useSyncExternalStore } from 'react'

export interface DrawerContext {
  entity: string
  id: string
  /** display name for the panel headers */
  label?: string
}

/** the Elevate-style scope: the current record, my book, or the team's */
export type DrawerScope = 'record' | 'mine' | 'team'

let context: DrawerContext | null = null
let ctx: string | null = null // the timeline's narrowing context (e.g. a deal id)
const subs = new Set<() => void>()
const notify = () => subs.forEach((f) => f())
const sub = (cb: () => void) => { subs.add(cb); return () => { subs.delete(cb) } }

/** Whether the CURRENT context was put there by the route rather than a page. */
let routeOwned = false

/** A PAGE binds the record it is showing. Always wins — see setFromRoute. */
export function setDrawerContext(next: DrawerContext | null) {
  context = next
  routeOwned = false
  ctx = null
  notify()
}

/**
 * The SHELL binds from the route. Deliberately weaker than a page's binding:
 * it never clears something a page owns.
 *
 * Without that asymmetry the shell clobbers every in-page selection. Measured:
 * `/crm?peek=o1` renders the peek, ListView binds the opportunity, and then the
 * shell — a PARENT, so its effect runs last — computed null for `/crm` and wiped
 * it. The drawer went empty on a record that was plainly on screen.
 *
 * A page's record is not always in the url and cannot always be: DeliveryCockpit
 * derives its project from query DATA, so no amount of route parsing would find
 * it. The shell handles the common case; a page overrides when it knows better.
 */
export function setDrawerContextFromRoute(next: DrawerContext | null) {
  if (next) {
    context = next
    routeOwned = true
    ctx = null
    notify()
    return
  }
  // The route denotes no record. Clear ONLY what the route itself put there.
  if (routeOwned) {
    context = null
    routeOwned = false
    ctx = null
    notify()
  }
}
export function setDrawerCtx(next: string | null) {
  ctx = next
  notify()
}
export function useDrawerContext(): DrawerContext | null {
  return useSyncExternalStore(sub, () => context)
}
export function useDrawerCtx(): string | null {
  return useSyncExternalStore(sub, () => ctx)
}

/* A page can bind a record and ask the shell to reveal the relevant lens
 * without importing either host's drawer. Same event seam as quick-create. */
const panelSubs = new Set<(key: string, toggle: boolean) => void>()
/**
 * Reveal a lens. `toggle` asks for a DISCLOSURE instead: pressing the control
 * again closes the panel it opened.
 *
 * Revealing is the default because that is what every existing caller means —
 * `CrumbBar` reveals the collection it just pinned into, the approvals inbox
 * reveals the decision it just bound. If either of those toggled, pinning a
 * second crumb while the panel was open would CLOSE the thing the pin was
 * supposed to show.
 *
 * `toggle` exists for #2328 ruling §8: the console's Recent & Pinned trigger is
 * in the header now, and the rail button it replaces was the only way a mouse
 * could close that panel on a desktop — the docked column has no close control
 * of its own, only the compact sheet does. A one-way opener would therefore have
 * removed a capability rather than moved it.
 */
export function requestDrawerPanel(key: string, options?: {
  toggle?: boolean
  /**
   * What the requester wants the lens to SHOW when it opens (#2502 D4): the
   * top bar's notifications button asks for the Timeline on "Your account" —
   * `{ channel: 'account' }`. Plain string pairs, read by the lens through
   * `useDrawerIntent(key)`; the drawer itself never reads them. A lens that
   * reads none ignores them, so passing one to any lens is harmless.
   */
  intent?: Readonly<Record<string, string>>
}) {
  if (options?.intent) {
    intentSeq += 1
    intents.set(key, { intent: { ...options.intent }, seq: intentSeq })
    notify()
  }
  panelSubs.forEach((open) => open(key, options?.toggle === true))
}

/* ── the intent a request carried (#2502 D4, Crew contracts v1.1) ───────────
 * The last intent asked of each lens, with a sequence number so the SAME intent
 * asked twice (a second press of the bell after the person changed channel in
 * the lens) is still a new request the lens can act on. Held here rather than
 * in the drawer because the drawer does not know what a lens's intent means —
 * it only opens the lens; the lens applies it. */
export interface DrawerIntent { intent: Readonly<Record<string, string>>; seq: number }
const intents = new Map<string, DrawerIntent>()
let intentSeq = 0
/** The last intent a request carried for `key`, or null if none ever did. */
export function useDrawerIntent(key: string): DrawerIntent | null {
  return useSyncExternalStore(sub, () => intents.get(key) ?? null)
}
export function subscribeDrawerPanel(open: (key: string, toggle: boolean) => void) {
  panelSubs.add(open)
  return () => { panelSubs.delete(open) }
}

/* ── which lens is open, PUBLISHED (#2328 ruling 74071 §8) ──────────────────
 * The drawer remains the single owner of that state: `DrawerShell` calls
 * `publishDrawerPanel` and nothing else does. This is a publication of one
 * state in the shape `--drawer-w` is already published from the same component,
 * not a second store — ruling §8's "one panel, one surface, one state" is
 * exactly what forbids the alternative, which is a trigger keeping its own copy
 * of open/closed and drifting from the panel it names.
 *
 * A host trigger needs it to be a disclosure at all: `aria-expanded`, the exact
 * `Open …`/`Close …` names #2325 established, and the toggle above all three
 * read this one value.
 */
let openPanel: string | null = null
export function publishDrawerPanel(key: string | null) {
  if (openPanel === key) return
  openPanel = key
  notify()
}
/** Is `key`'s panel on screen right now? */
export function useDrawerPanelOpen(key: string): boolean {
  return useSyncExternalStore(sub, () => openPanel === key)
}

/* explicit scope picks are PER TENANT (Elevate remembers per-lens) and
 * persisted; null ⇒ resolve record→persona default */
const SCOPE_KEY = 'solum.drawer.scope'
let scopes: Record<string, DrawerScope> = (() => {
  try { return JSON.parse(localStorage.getItem(SCOPE_KEY) ?? '{}') as Record<string, DrawerScope> } catch { return {} }
})()
export function setDrawerScope(tenant: string, next: DrawerScope | null) {
  scopes = { ...scopes }
  if (next === null) delete scopes[tenant]
  else scopes[tenant] = next
  try { localStorage.setItem(SCOPE_KEY, JSON.stringify(scopes)) } catch { /* private mode */ }
  notify()
}
export function useDrawerScope(tenant: string): DrawerScope | null {
  return useSyncExternalStore(sub, () => scopes[tenant] ?? null)
}

/* ── Route-derived binding (#329/#330) ──────────────────────────────────────
 * The SHELL binds the drawer from the active route, so no detail page has to
 * remember to call setDrawerContext. With 73 surfaces exactly ONE remembered,
 * which is how the sidepane ended up as global chrome with nothing behind it: a
 * contract that must be repeated in every new file is one forgotten in most.
 *
 * NOTHING module-specific lives here. The record's entity comes from the route
 * itself — either as a param (`/e/$entity/$id`, the universal surface) or as the
 * route's OWN `staticData.entity`, declared next to the route definition. A new
 * detail route brings its own binding; this file never learns about tenders.
 *
 * Its limit, stated where it is enforced: this works where the record IS the
 * route. A surface that picks its record in-page must still bind itself, and
 * should, because the shell genuinely cannot know.
 */
export type RouteLike = {
  params?: Record<string, string | undefined>
  staticData?: { entity?: string }
}

/** The record the active route denotes, or null when it denotes none. */
export function bindingForRoute(matches: readonly RouteLike[]): DrawerContext | null {
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i]
    const id = m?.params?.id
    if (!id) continue
    // the url carries the entity (universal surface), else the route declares it
    const entity = m?.params?.entity ?? m?.staticData?.entity
    if (entity) return { entity, id }
  }
  return null
}
